import React, { useEffect, useState } from 'react';
import Button from '../../../../../../components/ui/buttons/Button';
import Pagination from '../../../../../../components/ui/pagination/Pagination';
import { camelizeKeys } from '../../../../../../utils/camelizeKeys';
import { Building, Trash2 } from 'lucide-react';
import { formatCurrency } from '../../../../../../utils/formatters';
import CustomRoundedButton from '../../../../../../components/ui/buttons/CustomRoundedButton';
import { useToast } from '../../../../../../components/ui/toast/ToastContext';
import { useConfirm } from '../../../../../../components/ui/confirm/ConfirmContext';
import { fetchWithCsrf } from '../../../../../../utils/fetchWithCsrf';
import RealEstateModal from '../../../../../../components/modals/properties/RealEstateModal';

const initialPagination = {
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
};

const RealEstateTab = ({ clientId, scope, canWrite }) => {
  const [total, setTotal] = useState(0);
  const [realEstates, setRealEstates] = useState([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [showCreateRealEstateModal, setShowCreateRealEstateModal] = useState(false);
  const [selectedRealEstateId, setShowEditRealEstateId] = useState(null);
  const { addToast } = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    fetchData(1);
  }, [clientId, scope]);

  const buildQuery = (params) =>
    Object.entries(params)
      .filter(([, v]) => v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');

  const fetchData = (page = 1) => {
    const query = buildQuery({ page, scope });

    fetch(`/app/planner/client/${clientId}/dashboard/properties/real_estates?${query}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);
        setRealEstates(camelData.realEstates);
        setPagination(camelData.pagination);
        setTotal(camelData.total);
      });
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'EXCLUIR IMÓVEL?',
      description: `Tem certeza que deseja excluir o imóvel?`,
      confirmText: 'EXCLUIR',
    });

    if (!ok) return;

    try {
      const res = await fetchWithCsrf(`/app/planner/client/${clientId}/dashboard/properties/real_estates/${id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao excluir o imóvel');
      }

      addToast({ type: 'success', message: 'Imóvel excluído com sucesso!' });
      fetchData(pagination.currentPage);
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  const handlePageChange = (newPage) => {
    fetchData(newPage);
  };

  const handleModalSuccess = () => {
    setShowCreateRealEstateModal(false);
    setShowEditRealEstateId(null);
    fetchData(pagination.currentPage);
  };

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Título */}
      <h2 className="text-xl text-blueDarker font-semibold">Lista de imóveis</h2>

      {realEstates.length > 0 ? (
        <div className="overflow-x-auto">
          {/* HEADER */}
          <div className="px-2">
            <div className="flex items-center gap-2 text-xs text-tealBase font-medium">
              <div className="w-12 shrink-0" />
              <div className="w-64 shrink-0">Identificação</div>
              <div className="w-40 shrink-0">Localidade</div>
              <div className="w-24 shrink-0">Tipo</div>
              <div className="w-24 shrink-0">Financiado</div>
              <div className="w-24 shrink-0">Natureza</div>
              <div className="flex-1">Valor de mercado efetivo</div>
              <div className="w-24 shrink-0 text-right" />
            </div>
          </div>

          {/* LISTA */}
          <div className="mt-1 flex flex-col gap-1">
            {realEstates.map((item, index) => (
              <React.Fragment key={index}>
                <div className="bg-blueLight rounded-xl px-3 py-3">
                  <div className="flex items-center gap-2 text-sm text-blueSteel">
                    {/* ÍCONE */}
                    <div className="w-12 shrink-0 flex justify-center">
                      <span className="w-9 h-9 flex items-center justify-center rounded-full bg-white">
                        <Building className="text-tealMedium" size={18} strokeWidth={2} />
                      </span>
                    </div>

                    {/* Identificação */}
                    <div
                      className="w-64 shrink-0 text-blueDarker font-semibold truncate text-base"
                      title={item.name || '—'}
                    >
                      {item.name || '—'}
                    </div>

                    {/* Localidade */}
                    <div className="w-40 shrink-0 truncate">
                      {item.city && item.state ? `${item.city}/${item.state}` : '—'}
                    </div>

                    {/* Tipo */}
                    <div className="w-24 shrink-0 truncate">{item.realEstateType || '—'}</div>

                    {/* Financiado */}
                    <div className="w-24 shrink-0 truncate">{item.financed ? 'Sim' : 'Não'}</div>

                    {/* Natureza */}
                    <div className="w-24 shrink-0 truncate">{item.isPersonal ? 'Particular' : 'Comum'}</div>

                    {/* Valor de mercado efetivo */}
                    <div className="flex-1 min-w-0 truncate">{formatCurrency(item.effectiveMarketValue)}</div>

                    {/* Ações */}
                    <div className="w-24 shrink-0 flex items-center justify-end gap-4 pr-1">
                      {canWrite && (
                        <>
                          <button onClick={() => handleDelete(item.id)} title="Excluir" disabled={scope === 'couple'}>
                            <Trash2
                              className={scope === 'couple' ? 'cursor-not-allowed text-gray-400' : 'text-redBase'}
                              size={18}
                            />
                          </button>
                          <CustomRoundedButton
                            disabled={scope === 'couple'}
                            size="sm"
                            onClick={() => setShowEditRealEstateId(item.id)}
                          >
                            EDITAR
                          </CustomRoundedButton>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* espaçador entre linhas */}
                <div className="h-2" />
              </React.Fragment>
            ))}
          </div>
          {pagination.totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      ) : (
        <p>Ainda sem registros.</p>
      )}

      {/* Footer */}
      <div className="mt-auto flex justify-between items-center">
        <div className="flex font-medium items-center gap-2">
          <span className="text-sm text-tealBase">TOTAL: </span>
          <span className="text-xl blueDark">{formatCurrency(total)}</span>
        </div>

        <div className="flex gap-3">
          {canWrite && (
            <Button disabled={scope == 'couple'} onClick={() => setShowCreateRealEstateModal(true)}>
              ADICIONAR IMÓVEL
            </Button>
          )}
        </div>
      </div>

      {showCreateRealEstateModal && (
        <RealEstateModal
          clientId={clientId}
          scope={scope}
          onClose={() => setShowCreateRealEstateModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {selectedRealEstateId && (
        <RealEstateModal
          clientId={clientId}
          realEstateId={selectedRealEstateId}
          scope={scope}
          onClose={() => setShowEditRealEstateId(null)}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default RealEstateTab;

// import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
// import Button from '../../../../../../components/ui/buttons/Button';
// import Pagination from '../../../../../../components/ui/pagination/Pagination';
// import { camelizeKeys } from '../../../../../../utils/camelizeKeys';
// import { Building, Trash2 } from 'lucide-react';
// import { formatCurrency } from '../../../../../../utils/formatters';
// import CustomRoundedButton from '../../../../../../components/ui/buttons/CustomRoundedButton';
// import { useToast } from '../../../../../../components/ui/toast/ToastContext';
// import { useConfirm } from '../../../../../../components/ui/confirm/ConfirmContext';
// import { fetchWithCsrf } from '../../../../../../utils/fetchWithCsrf';
// import RealEstateModal from '../../../../../../components/modals/properties/RealEstateModal';

// const initialPagination = {
//   currentPage: 1,
//   totalPages: 1,
//   totalCount: 0,
// };

// const COL_KEYS = ['icon', 'name', 'locale', 'type', 'financed', 'nature', 'marketValue', 'actions'];

// const RealEstateTab = ({ clientId, scope }) => {
//   const [total, setTotal] = useState(0);
//   const [realEstates, setRealEstates] = useState([]);
//   const [pagination, setPagination] = useState(initialPagination);
//   const [showCreateRealEstateModal, setShowCreateRealEstateModal] = useState(false);
//   const [selectedRealEstateId, setShowEditRealEstateId] = useState(null);
//   const { addToast } = useToast();
//   const confirm = useConfirm();
//   const theadRef = useRef(null);
//   const [colWidths, setColWidths] = useState(null);

//   useEffect(() => {
//     console.log('[RealEstateTab] mount/fetch', { clientId, scope });
//     fetchData(1);
//   }, [clientId, scope]);

//   // mede as larguras naturais dos TH e "congela"
//   useLayoutEffect(() => {
//     // só mede se ainda não temos colWidths E já temos dados na tela
//     if (colWidths) return;
//     if (!realEstates || realEstates.length === 0) return;

//     // espera um frame para layout “assentar” (fonts, etc.)
//     const raf = requestAnimationFrame(() => {
//       const widths = measureHeaderCols();
//       if (widths) {
//         console.log('[useLayoutEffect] freezing col widths (px):', widths);
//         setColWidths(widths);
//       } else {
//         console.warn('[useLayoutEffect] widths null, not freezing');
//       }
//     });

//     return () => cancelAnimationFrame(raf);
//   }, [realEstates, colWidths]);

//   const buildQuery = (params) =>
//     Object.entries(params)
//       .filter(([, v]) => v !== '')
//       .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
//       .join('&');

//   const fetchData = (page = 1) => {
//     console.log('[fetchData] requesting page:', page);
//     const query = buildQuery({ page, scope });

//     fetch(`/app/planner/client/${clientId}/dashboard/properties/real_estates?${query}`)
//       .then((res) => res.json())
//       .then((data) => {
//         const camelData = camelizeKeys(data);
//         console.log('[fetchData] received', {
//           page,
//           count: camelData.realEstates?.length,
//           totalPages: camelData.pagination?.totalPages,
//         });
//         setRealEstates(camelData.realEstates);
//         setPagination(camelData.pagination);
//         setTotal(camelData.total);
//       });
//   };

//   const measureHeaderCols = () => {
//     if (!theadRef.current) {
//       console.warn('[measureHeaderCols] theadRef.current is null');
//       return null;
//     }
//     const ths = theadRef.current.querySelectorAll('th[data-col]');
//     if (!ths.length) {
//       console.warn('[measureHeaderCols] no th[data-col] found');
//       return null;
//     }

//     const widthsPx = {};
//     ths.forEach((th) => {
//       const key = th.getAttribute('data-col');
//       const rect = th.getBoundingClientRect();
//       const cs = window.getComputedStyle(th);
//       const padL = parseFloat(cs.paddingLeft) || 0;
//       const padR = parseFloat(cs.paddingRight) || 0;

//       // width do CONTEÚDO (colgroup trabalha com content width)
//       const contentWidth = Math.max(0, rect.width - padL - padR);

//       widthsPx[key] = Math.ceil(contentWidth);
//       console.log(`[measure] ${key}`, {
//         rectWidth: Math.ceil(rect.width),
//         padL,
//         padR,
//         contentWidth: Math.ceil(contentWidth),
//       });
//     });

//     // mínimos pra estabilidade:
//     widthsPx.icon = Math.max(widthsPx.icon || 48, 48);
//     widthsPx.actions = Math.max(widthsPx.actions || 96, 96);

//     // fallback seguro para quaisquer undefined
//     COL_KEYS.forEach((k) => {
//       if (!widthsPx[k] || Number.isNaN(widthsPx[k])) widthsPx[k] = 120;
//     });

//     console.table(widthsPx);
//     return widthsPx;
//   };

//   const handleDelete = async (id) => {
//     const ok = await confirm({
//       title: 'EXCLUIR IMÓVEL?',
//       description: `Tem certeza que deseja excluir o imóvel?`,
//       confirmText: 'EXCLUIR',
//     });

//     if (!ok) return;

//     try {
//       const res = await fetchWithCsrf(`/app/planner/client/${clientId}/dashboard/properties/real_estates/${id}`, {
//         method: 'DELETE',
//         headers: {
//           Accept: 'application/json',
//         },
//       });

//       if (!res.ok) {
//         const json = await res.json();
//         throw new Error(json.message || 'Erro ao excluir o imóvel');
//       }

//       addToast({ type: 'success', message: 'Imóvel excluído com sucesso!' });
//       fetchData(pagination.currentPage);
//     } catch (err) {
//       addToast({ type: 'error', message: err.message });
//     }
//   };

//   const handlePageChange = (newPage) => {
//     console.log('[handlePageChange] ->', newPage);
//     fetchData(newPage);
//   };

//   const handleModalSuccess = () => {
//     setShowCreateRealEstateModal(false);
//     setShowEditRealEstateId(null);
//     fetchData(pagination.currentPage);
//   };

//   return (
//     <div className="h-full flex flex-col gap-4">
//       {/* Título */}
//       <h2 className="text-xl text-blueDarker font-semibold">Lista de imóveis</h2>

//       {realEstates.length > 0 ? (
//         <div className="overflow-x-hidden">
//           <table className="w-full table-fixed">
//             <colgroup>
//               <col style={{ width: `56px` }} />
//               <col style={{ width: `36%` }} />
//               <col style={{ width: `16%` }} />
//               <col style={{ width: `10%` }} />
//               <col style={{ width: `8%` }} />
//               <col style={{ width: `10%` }} />
//               <col style={{ width: `20%` }} />
//               <col style={{ width: `112px` }} />
//             </colgroup>

//             <thead ref={theadRef}>
//               <tr className="text-xs text-tealBase font-medium">
//                 <th data-col="icon" className="px-2 py-2 text-left"></th>
//                 <th data-col="name" className="px-2 py-2 text-left">
//                   Identificação
//                 </th>
//                 <th data-col="locale" className="px-2 py-2 text-left">
//                   Localidade
//                 </th>
//                 <th data-col="type" className="px-2 py-2 text-left">
//                   Tipo
//                 </th>
//                 <th data-col="financed" className="px-2 py-2 text-left">
//                   Financiado
//                 </th>
//                 <th data-col="nature" className="px-2 py-2 text-left">
//                   Natureza
//                 </th>
//                 <th data-col="marketValue" className="px-2 py-2 text-left">
//                   Valor de mercado
//                 </th>
//                 <th data-col="actions" className="px-2 py-2 text-right"></th>
//               </tr>
//             </thead>

//             <tbody>
//               {realEstates.map((item) => (
//                 <tr key={item.id} className="bg-blueLight">
//                   {/* Ícone */}
//                   <td className="pl-2 pr-2 py-3">
//                     <div className="flex justify-center">
//                       <span className="w-9 h-9 flex items-center justify-center rounded-full bg-white">
//                         <Building className="text-tealMedium" size={18} strokeWidth={2} />
//                       </span>
//                     </div>
//                   </td>

//                   {/* Identificação: clamp em 2 linhas */}
//                   <td className="px-2 py-3">
//                     <div
//                       title={item.name || '—'}
//                       className="text-blueDarker font-semibold text-base overflow-hidden"
//                       style={{
//                         display: '-webkit-box',
//                         WebkitLineClamp: 1,
//                         WebkitBoxOrient: 'vertical',
//                       }}
//                     >
//                       {item.name || '—'}
//                     </div>
//                   </td>

//                   {/* Demais colunas: pode usar truncate OU quebra normal */}
//                   <td className="px-2 py-3">
//                     <div className="truncate">{item.city && item.state ? `${item.city}/${item.state}` : '—'}</div>
//                   </td>
//                   <td className="px-2 py-3">
//                     <div className="truncate">{item.realEstateType || '—'}</div>
//                   </td>
//                   <td className="px-2 py-3">
//                     <div className="truncate">{item.financed ? 'Sim' : 'Não'}</div>
//                   </td>
//                   <td className="px-2 py-3">
//                     <div className="truncate">{item.isPersonal ? 'Particular' : 'Comum'}</div>
//                   </td>
//                   <td className="px-2 py-3">
//                     <div className="truncate">{formatCurrency(item.marketValue)}</div>
//                   </td>

//                   {/* Ações */}
//                   <td className="pl-2 pr-0 py-3">
//                     <div className="w-full flex items-center justify-end gap-3">
//                       <button onClick={() => handleDelete(item.id)} title="Excluir" disabled={scope === 'couple'}>
//                         <Trash2
//                           className={scope === 'couple' ? 'cursor-not-allowed text-gray-400' : 'text-redBase'}
//                           size={18}
//                         />
//                       </button>
//                       <CustomRoundedButton
//                         disabled={scope === 'couple'}
//                         size="sm"
//                         onClick={() => setShowEditRealEstateId(item.id)}
//                       >
//                         EDITAR
//                       </CustomRoundedButton>
//                     </div>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>

//           <style>{`
//             table { border-collapse: separate; border-spacing: 0 8px; }
//             tbody tr { border-radius: 12px; overflow: hidden; }
//             tbody tr > td:first-child { border-top-left-radius: 12px; border-bottom-left-radius: 12px; }
//             tbody tr > td:last-child  { border-top-right-radius: 12px; border-bottom-right-radius: 12px; }
//           `}</style>

//           {pagination.totalPages > 1 && (
//             <div className="mt-4">
//               <Pagination
//                 currentPage={pagination.currentPage}
//                 totalPages={pagination.totalPages}
//                 onPageChange={handlePageChange}
//               />
//             </div>
//           )}
//         </div>
//       ) : (
//         <p>Ainda sem registros.</p>
//       )}
//       {/* Footer */}
//       <div className="mt-auto flex justify-between items-center">
//         <div className="flex font-medium items-center gap-2">
//           <span className="text-sm text-tealBase">TOTAL: </span>
//           <span className="text-xl blueDark">{formatCurrency(total)}</span>
//         </div>

//         <div className="flex gap-3">
//           <Button disabled={scope == 'couple'} onClick={() => setShowCreateRealEstateModal(true)}>
//             ADICIONAR IMÓVEL
//           </Button>
//         </div>
//       </div>

//       {showCreateRealEstateModal && (
//         <RealEstateModal
//           clientId={clientId}
//           scope={scope}
//           onClose={() => setShowCreateRealEstateModal(false)}
//           onSuccess={handleModalSuccess}
//         />
//       )}

//       {selectedRealEstateId && (
//         <RealEstateModal
//           clientId={clientId}
//           realEstateId={selectedRealEstateId}
//           scope={scope}
//           onClose={() => setShowEditRealEstateId(null)}
//           onSuccess={handleModalSuccess}
//         />
//       )}
//     </div>
//   );
// };

// export default RealEstateTab;
